'use client';

// Deep imports on purpose: catalog-ui components import this hook, so going
// through the catalog-ui barrel would create a cycle between the two
// libraries (and React Fast Refresh reads every barrel export on evaluation).
import {
  useCatalogContext,
  useRequireCiC,
} from '@dealerportal/catalog-ui/Contexts/CatalogContext';
import {
  GROUP_REF_SRP_PRIORITY,
  useCiCSearch,
} from '@dealerportal/catalog-ui/Hooks/useCiCSearch';
import type { PaginatedProduct } from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import {
  cicCharacteristicCode,
  isValidQueryString,
  toCiCMerchandising,
} from '@dealerportal/utilities';
import { useEffect, useState } from 'react';

export type ProductSearchResults = {
  total: number;
  products: PaginatedProduct[];
  loading: boolean;
};

// Convert query string to CiC characteristics format
const convertQueryToCharacteristics = (
  query: string
): Record<string, string[]> => {
  const characteristics: Record<string, string[]> = {};

  if (!query) return characteristics;

  // Parse query string parameters
  const queryParams = new URLSearchParams(query);

  queryParams.forEach((value, key) => {
    switch (key) {
      case 'type':
        // strip special characters including hyphens and lowercase (e.g. "luxury-vinyl" -> "luxuryvinyl")
        characteristics['type'] = value
          .split(',')
          .map((type) =>
            type.indexOf('.') === -1
              ? type.toLowerCase().replace(/[^a-zA-Z0-9]/g, '')
              : type
          );
        break;
      case 'brand':
        characteristics['brand'] = value.split(',');
        break;
      case 'line':
        characteristics['line'] = value.split(',');
        break;
      case 'tags':
      case 'feature':
        characteristics['merchandisings'] = value
          .split(',')
          .map(toCiCMerchandising);
        break;
      case 'excludeUpid':
      case 'spreadProducts':
        break;
      default:
        if (value && key !== 'numResults') {
          characteristics[cicCharacteristicCode(key)] = value.split(',');
        }
        break;
    }
  });

  return characteristics;
};

export function useProductSearch(
  query: string | string[] | (() => string | string[]),
  numResults = 5,
  excludeVariants = false
) {
  const { useCiC, catalogVersionId, loaded, searchWithoutGroupRef } =
    useCatalogContext();
  const [results, setResults] = useState<ProductSearchResults>({
    products: [],
    total: 0,
    loading: true, // Added for loading skeleton UI
  });
  useRequireCiC(useCiC);

  // Initialize CiC search hook
  const { search } = useCiCSearch();

  query = typeof query === 'function' ? query() : query;
  query = Array.isArray(query) ? query.join('&') : query;
  query = query.startsWith('?') ? query : `?${query}`;

  useEffect(() => {
    if (!isValidQueryString(query as string)) {
      // if invalid, stop loading.
      setResults((prev) => ({ ...prev, loading: false }));
      return;
    }
    // if valid, start loading
    setResults((prev) => ({ ...prev, loading: true }));

    const abortController = new AbortController();
    const signal = abortController.signal;

    // Use CiC search if useCiC is true and catalogVersionId is provided
    if (useCiC) {
      const performCicSearch = async () => {
        logger.info('Performing CiC search with query:', query);
        try {
          // Convert query to characteristics
          const characteristics = convertQueryToCharacteristics(
            query as string
          );

          // Call CiC search (use page 1 for simple search)
          const result = await search({
            catalogVersionId: catalogVersionId ?? '-1',
            initialPage: 1,
            characteristics,
            excludeFilters: true,
            pageSize: numResults,
            excludeVariants,
            groupRefs:
              !characteristics['spreadProducts'] && !searchWithoutGroupRef
                ? [GROUP_REF_SRP_PRIORITY]
                : undefined, // Group by srp_priority if spreadProducts and searchWithoutGroupRef are false
          });

          if (!signal.aborted) {
            // Transform result to match ProductSearchResults interface
            setResults({
              products: result.products as PaginatedProduct[],
              total: result.colorCount || 0,
              loading: false,
            });
          }
        } catch (error) {
          if (!signal.aborted) {
            logger.withError(error).error('CiC search error:');
            setResults((prev) => ({ ...prev, loading: false }));
          }
        }
      };
      if (catalogVersionId && loaded) {
        performCicSearch();
      }
    } else {
      // Use traditional search
      fetch(`/api/products/search${query}&numResults=${numResults}`, { signal })
        .then((res) => res.json())
        .then((json) => {
          if (!signal.aborted) {
            setResults({ ...json, loading: false });
          }
        })
        .catch((error) => {
          if (!signal.aborted) {
            logger.withError(error).error('Product search error:');
            setResults((prev) => ({ ...prev, loading: false }));
          }
        });
    }

    return () => {
      abortController.abort();
    };
  }, [query, numResults, useCiC, catalogVersionId, search, loaded]);

  return results;
}
